import { supabase } from '@rehub/supabase';
import { uploadToStorage } from './supabase';
import type {
  CreatePreviewResponse,
  PollPreviewResponse,
  PreviewPageData,
  ListingPreviewEventType,
  ListingData,
} from '../types/listing-funnel';

// ============================================================
// Session ID — persisted across the funnel for account handoff
// ============================================================

const SESSION_KEY = 'listing_funnel_session_id';

export function getListingSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// ============================================================
// API Calls (via Supabase Edge Functions)
// ============================================================

export async function createListingPreview(
  url: string
): Promise<CreatePreviewResponse> {
  const sessionId = getListingSessionId();

  const { data, error } = await supabase.functions.invoke('listing-preview-create', {
    body: { url, sessionId },
  });

  if (error) {
    throw new Error(error.message ?? 'Failed to create preview');
  }

  if (data?.error) {
    throw Object.assign(new Error(data.error), {
      code: data.error,
      detail: data.detail,
      message: data.message,
    });
  }

  return data as CreatePreviewResponse;
}

export async function pollListingPreview(jobId: string): Promise<PollPreviewResponse> {
  const { data, error } = await supabase.functions.invoke('listing-preview-poll', {
    body: { jobId },
  });

  if (error) {
    throw new Error(error.message ?? 'Failed to poll preview');
  }

  if (data?.error && data.status === 'failed') {
    return data as PollPreviewResponse;
  }

  if (data?.error) {
    throw Object.assign(new Error(data.error), { code: data.error });
  }

  return data as PollPreviewResponse;
}

// ============================================================
// Fetch completed preview by share slug (for the preview page)
// ============================================================

export async function fetchListingPreviewBySlug(shareSlug: string): Promise<PreviewPageData | null> {
  const { data, error } = await supabase
    .from('listing_preview_jobs')
    .select('*')
    .eq('share_slug', shareSlug)
    .in('status', ['completed', 'cached'])
    .maybeSingle();

  if (error || !data) return null;

  // Refresh signed URL if we have a storage path
  let videoUrl = data.preview_video_url;
  if (data.preview_video_path) {
    const { data: signedData } = await supabase.storage
      .from('imagemotion-uploads')
      .createSignedUrl(data.preview_video_path, 60 * 60 * 24 * 7);
    if (signedData?.signedUrl) {
      videoUrl = signedData.signedUrl;
    }
  }

  if (!videoUrl) return null;

  return {
    jobId: data.id,
    videoUrl,
    listing: {
      title: data.listing_title,
      address: data.listing_address,
      imageUrls: data.images_used ?? [],
    },
    images: data.images_used ?? [],
    shareSlug: data.share_slug,
    createdAt: data.created_at,
  };
}

// ============================================================
// Analytics Events
// ============================================================

export async function logListingEvent(
  eventType: ListingPreviewEventType,
  metadata?: Record<string, any>,
  jobId?: string
): Promise<void> {
  try {
    const sessionId = getListingSessionId();
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.rpc('log_listing_preview_event', {
      p_event_type: eventType,
      p_session_id: sessionId,
      p_job_id: jobId ?? null,
      p_user_id: user?.id ?? null,
      p_metadata: metadata ?? {},
    });
  } catch {
    // Analytics should never break the user experience
  }
}

// ============================================================
// Account Handoff — claim anonymous previews after signup
// ============================================================

export async function claimListingPreviews(): Promise<number> {
  const sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) return 0;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { data, error } = await supabase.rpc('claim_listing_previews', {
    p_user_id: user.id,
    p_session_id: sessionId,
  });

  if (error) {
    console.warn('Failed to claim listing previews:', error.message);
    return 0;
  }

  return data?.claimed ?? 0;
}

// ============================================================
// Upload images for public funnel (no auth required)
// Uploads directly to Supabase Storage, then passes URLs to the
// Edge Function to create the project.
// ============================================================

export async function createListingPreviewFromUpload(
  files: File[],
  projectName?: string,
  onProgress?: (uploaded: number, total: number) => void,
  aspectRatio?: '16:9' | '9:16'
): Promise<CreatePreviewResponse> {
  const sessionId = getListingSessionId();
  const uploadedUrls: string[] = [];

  for (let i = 0; i < files.length; i++) {
    onProgress?.(i, files.length);

    const file = files[i];
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `listing-preview-uploads/${sessionId}/${Date.now()}-${i}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from('imagemotion-uploads')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadErr) {
      console.error(`Image ${i} upload failed:`, uploadErr.message);
      continue;
    }

    const { data: signedData } = await supabase.storage
      .from('imagemotion-uploads')
      .createSignedUrl(path, 60 * 60 * 24 * 7);

    if (signedData?.signedUrl) {
      uploadedUrls.push(signedData.signedUrl);
    }
  }

  if (uploadedUrls.length === 0) {
    throw Object.assign(new Error('upload_failed'), { code: 'upload_failed' });
  }

  onProgress?.(files.length, files.length);

  const { data, error } = await supabase.functions.invoke('listing-preview-upload', {
    body: { sessionId, projectName, imageUrls: uploadedUrls, aspectRatio: aspectRatio ?? '16:9' },
  });

  // supabase.functions.invoke puts non-2xx responses in error,
  // but the response body may contain our structured error
  if (error) {
    if (data?.error) {
      throw Object.assign(new Error(data.error), {
        code: data.error,
        detail: data.detail,
        message: data.message ?? data.detail,
      });
    }
    throw Object.assign(new Error('generic'), { code: 'generic' });
  }

  if (data?.error) {
    throw Object.assign(new Error(data.error), {
      code: data.error,
      detail: data.detail,
      message: data.message ?? data.detail,
    });
  }

  return data as CreatePreviewResponse;
}

// ============================================================
// Extract-only mode — for authenticated "Create from URL" flow
// ============================================================

export interface ExtractResult {
  listing: ListingData;
  images: string[];
  provider: string;
}

export async function extractListingImages(url: string): Promise<ExtractResult> {
  const { data, error } = await supabase.functions.invoke('listing-preview-create', {
    body: { url, extractOnly: true },
  });

  if (error) {
    throw new Error(error.message ?? 'Failed to extract listing');
  }

  if (data?.error) {
    throw Object.assign(new Error(data.error), {
      code: data.error,
      detail: data.detail,
    });
  }

  return data as ExtractResult;
}

/**
 * Downloads an image from an external CDN URL and re-uploads it
 * to Supabase Storage so the Railway worker can access it via
 * signed URLs.
 */
export async function downloadAndUploadImage(
  externalUrl: string,
  userId: string
): Promise<{ url: string; path: string }> {
  const response = await fetch(externalUrl);
  if (!response.ok) {
    throw new Error(`Failed to download image: ${response.status}`);
  }
  const blob = await response.blob();
  const ext = externalUrl.match(/\.(jpe?g|png|webp)/i)?.[1] ?? 'jpg';
  const file = new File(
    [blob],
    `listing-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`,
    { type: blob.type || 'image/jpeg' }
  );
  return uploadToStorage(file, 'images', userId);
}

// ============================================================
// Download — only available for authenticated users
// ============================================================

export async function downloadPreviewVideo(videoUrl: string, filename?: string): Promise<void> {
  const name = filename ?? 'imagemotion-preview.mp4';
  const marker = '/object/sign/imagemotion-uploads/';
  const idx = videoUrl.indexOf(marker);

  try {
    let blob: Blob | null = null;
    if (idx !== -1) {
      const pathWithQuery = videoUrl.slice(idx + marker.length);
      const path = decodeURIComponent(pathWithQuery.split('?')[0]);
      const { data, error } = await supabase.storage
        .from('imagemotion-uploads')
        .download(path);
      if (!error && data) blob = data;
    }
    if (!blob) {
      const res = await fetch(videoUrl);
      if (!res.ok) throw new Error('Download failed');
      blob = await res.blob();
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 2000);
  } catch {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = name;
    a.rel = 'noopener';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 2000);
  }
}
